import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { IsoStringToDatePipe } from '../../../pipes/iso-string-to-date.pipe';
import { ContactImportItem, ImportService, UploadBatch } from '../../../services/import.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { getGridTemplate } from '../../../utils/utils';
import { SnackbarComponent } from '../../misc/snackbar/snackbar.component';
import { ContactImportComponent } from '../contact-import/contact-import.component';
import { O365AuthService } from '../../../services/o365-auth.service';

@Component({
  selector: "app-import-contacts",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatButtonModule,
    IsoStringToDatePipe,
    MatCardModule,
    MatTableModule,
    MatInputModule,
    MatPaginatorModule,
    MatSortModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatCardModule,
    ContactImportComponent,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    MatSnackBarModule
  ],
  templateUrl: "./import-contacts.component.html",
  styleUrl: "./import-contacts.component.scss",
})
export class ImportContactsComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  readonly _snackBar = inject(MatSnackBar);
  readonly importService = inject(ImportService);
  readonly refDataService = inject(ReferenceDataService);
  readonly authService = inject(O365AuthService);

  readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
  readonly allTopicOptions = computed(() => this.allTopics());
  readonly allStakeholderGroups = this.refDataService.getContactStakeholdeGroups();
  readonly allStakeholderGroupOptions = computed(() => this.allStakeholderGroups());

  loading = signal(false);
  pageIndex = signal(0);
  pageSize = signal(10);
  data = signal<ContactImportItem[]>([]);
  pagedData = computed(() => {
    const start = this.pageIndex() * this.pageSize();
    const end = start + this.pageSize();
    return this.data().slice(start, end);
  });

  batches = this.importService.batches; // http resource signal for batches
  batch: UploadBatch | null = null;
  batchLoaded = signal<boolean>(false);
  batchId = signal<number | null>(null);
  batchSelectControl: FormControl;
  hideEmptyBatches = signal(true);

  // allColumns = [
  //   { key: "firstName", label: "First name", type: "text" },
  //   { key: "lastName", label: "Last name", type: "text" },
  //   { key: "email", label: "Email", type: "text" },
  //   { key: "jobTitle", label: "Job title", type: "text" },
  //   { key: "organisation", label: "Organisation", type: "chips" },
  //   { key: "stakeholderGroups", label: "Stakeholder groups", type: "chips" },
  //   { key: "topicsOfInterest", label: "Topics of interest", type: "chips" },
  //   { key: "actions", label: "Actions", type: "text" },
  // ];

  // firstRowColumns = ['firstName', 'lastName', 'email', 'jobTitle', 'organisation'];
  // secondRowColumns = ['topicsOfInterest', 'stakeholderGroups'];

  dataSource = new MatTableDataSource<ContactImportItem>(this.data());

  toggleEdit(row: any) {
    row.editing = !row.editing;
  }

  constructor(private readonly fb: FormBuilder, private router: Router, private route: ActivatedRoute) {
    this.batchSelectControl = this.fb.control("");

    // React to changes in batches and route param to set the selected batch
    effect(() => {
      console.debug("batches changed", this.batches.value());
      const batches = this.batches.value();
      const batchId = this.batchId();
      if (batches && batchId) {
        const batch = batches.find(b => b.id === +batchId);
        if (batch) {
          console.log("selected batch", batch);
          this.batch = batch;
          this.batchLoaded.set(false);
          this.loadBatchItems();
          this.batchSelectControl.setValue(batch);
        } else {
          console.warn("Batch not found for id", batchId);
          this.router.navigate(['/import']);
        }
      }
    });
  }

  ngOnInit(): void {
    this.importService.refreshBatches();

    this.batchSelectControl.valueChanges.subscribe((batch) => {
      console.debug("batchSelectControl changed", batch);
      this.batchId.set(batch ? batch.id : null);
      if (batch) {
        this.router.navigate(['/import', batch.id]);
      }
    });

    const batchId = this.route.snapshot.paramMap.get('id');
    this.batchId.set(batchId ? +batchId : null);

    // const batchId = this.route.snapshot.paramMap.get('id');
    // console.debug("ngOnInit", batchId);
    //   this.batch = v;
    //   this.batchLoaded.set(false);
    //   this.loadBatchItems();
    //   console.log("selected batch", v);

  }

  // triggered by UI button
  loadBatchItems(): void {
    console.debug("loading batch", this.batchSelectControl.value);
    this.loading.set(true);
    this.importService
      .getBatchItemsById(this.batch!.id)
      .subscribe({
        next: (v) => {
          this.loading.set(false);
          this.batchLoaded.set(true);
          this.data.set(v);
          this.batch!.unprocessedCount = v.length;
          this.pageIndex.set(0);
        },
        error: (e) => {
          console.error("error", e);
          this.loading.set(false);
          this.batchLoaded.set(false);
        },
      });
  }

  /** triggered by import component */
  reloadItems(): void {
    console.debug('reloadItems');
    this.loadBatchItems();
  }

  deleteBatch() {
    console.debug('deleteBatch');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete batch',
        message: 'Are you sure you want to delete this batch?'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.importService.deleteBatch(this.batch!.id).subscribe({
          next: () => {
            console.debug('batch deleted');
            this._snackBar.openFromComponent(SnackbarComponent, {
              data: {
                message: `✅ Batch deleted`,
                link: undefined
              },
              duration: 3000
            });
            this.router.navigate(['/import']);
          },
          error: (e) => {
            console.error('error deleting batch', e);
            this._snackBar.openFromComponent(SnackbarComponent, {
              data: {
                message: `❌ Error deleting batch: ${e}`,
                link: undefined
              },
              duration: 5000
            });
          }
        })
      }
    });
  }

  onPage(event: PageEvent) {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }


  /**
   * creates the template for the table columns
   */
  // get gridTemplate() {
  //   return getGridTemplate(this.firstRowColumns, this.allColumns);
  // }
}
